import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import ScrollingText from './ScrollingText';
import { Song, Album } from './types';
import { Maximize2, Minimize2 } from 'lucide-react';

interface MobileSongInfoProps {
  currentSong: Song | null;
  currentAlbum: Album | null;
  coverArt: string;
  isPlaying: boolean;
  albumButtonRef: React.RefObject<HTMLButtonElement>;
  setShowAlbums: (show: boolean) => void;
  showAlbums: boolean;
  setShowPlaylist: (show: boolean) => void;
  setShowTooltip: (show: boolean) => void;
}

const MobileSongInfo: React.FC<MobileSongInfoProps> = ({ 
  currentSong, 
  currentAlbum, 
  coverArt, 
  isPlaying, 
  albumButtonRef, 
  setShowAlbums, 
  showAlbums, 
  setShowPlaylist, 
  setShowTooltip 
}) => {
  // State to track fullscreen status
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Add event listener to handle fullscreen changes from outside the button (like Escape key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isInFullScreen = 
        document.fullscreenElement || 
        (document as any).webkitFullscreenElement || 
        (document as any).mozFullScreenElement || 
        (document as any).msFullscreenElement;
      
      setIsFullscreen(!!isInFullScreen);
    };

    // Add event listeners for all vendor prefixes
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    
    return () => {
      // Remove event listeners for all vendor prefixes
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Function to toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    // Get the document element
    const docEl = document.documentElement;
    
    // Check if we're in fullscreen mode
    const isInFullScreen = 
      document.fullscreenElement || 
      (document as any).webkitFullscreenElement || 
      (document as any).mozFullScreenElement || 
      (document as any).msFullscreenElement;
    
    if (!isInFullScreen) {
      // Enter fullscreen - handle vendor prefixes
      const requestFullScreen = 
        docEl.requestFullscreen || 
        (docEl as any).mozRequestFullScreen || 
        (docEl as any).webkitRequestFullscreen || 
        (docEl as any).msRequestFullscreen;
        
      if (requestFullScreen) {
        requestFullScreen.call(docEl)
          .then(() => {
            setIsFullscreen(true);
          })
          .catch(err => {
            console.error(`Error attempting to enable fullscreen: ${err.message}`);
          });
      }
    } else {
      // Exit fullscreen - handle vendor prefixes
      const exitFullScreen = 
        document.exitFullscreen || 
        (document as any).mozCancelFullScreen || 
        (document as any).webkitExitFullscreen || 
        (document as any).msExitFullscreen;
        
      if (exitFullScreen) {
        exitFullScreen.call(document)
          .then(() => {
            setIsFullscreen(false);
          })
          .catch(err => {
            console.error(`Error attempting to exit fullscreen: ${err.message}`);
          });
      }
    }
  }, []);

  // If either element doesn't exist or song is null, don't render anything
  if (!currentSong) return null;

  return (
    <div id="mobile-song-info-container" className="px-3 py-2 relative !justify-between">
      {/* Fullscreen button - Only shown on small screens */}
      <button 
        onClick={toggleFullscreen}
        className="relative order-1 text-white sm:hidden z-10 p-2 bg-black/40 hover:bg-black/60 rounded-full transition-colors touch-target"
        aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
      >
        {isFullscreen ? 
          <Minimize2 size={20} className="stroke-white" /> : 
          <Maximize2 size={20} className="stroke-white" />
        }
      </button>
      
      <div className="flex items-center space-x-3">
        <motion.button
          ref={albumButtonRef}
          onClick={() => {
            setShowAlbums(!showAlbums);
            setShowPlaylist(false);
            setShowTooltip(false);
          }}
          animate={{
            scale: isPlaying ? [1, 1.2, 1] : 1
          }}
          transition={{ duration: 0.5 }}
          className="relative w-[60px] h-[60px] bg-white overflow-hidden touch-target"
        >
          {coverArt ? (
            <img 
              src={coverArt} 
              alt={currentAlbum?.name || 'Unknown Album'}
              className="w-full h-full object-cover filter grayscale"
            />
          ) : (
            <div className="w-full h-full bg-zinc-800" />
          )}
        </motion.button>
        <div>
          <ScrollingText 
            text={currentSong.name} 
            className="text-xl uppercase tracking-wider font-bold"
          />
          <ScrollingText 
            text={currentAlbum?.name || 'Unknown Album'} 
            className="text-sm text-gray-400 uppercase tracking-wider"
          />
        </div>
      </div>
    </div>
  );
};

export default MobileSongInfo; 